import React, { useMemo } from 'react';
import type { ReactNode } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { CurrencyCode } from '@shared/hooks/useGeoLocale';
import type { CurrencyRateData } from '@apps/card/hooks/useCurrencyConversionRate';
import { parseOrderCurrencyConversionData } from '@apps/card/utils/orderCurrencyConversion';
import { DEFAULT_CURRENCY_CONVERSION_RATE } from '@apps/card/constants/price';

export type OrderPriceConversion = Readonly<{
  /** The rate committed to the order at purchase, not today's rate. */
  rate: CurrencyRateData;
  /** The currency the order was paid in. */
  currency: CurrencyCode;
}>;

const [Provider, useOrderPriceConversion] = createContext<OrderPriceConversion | null>({
  name: 'OrderPriceConversion',
  strict: false,
  initialValue: null,
});

/**
 * The conversion prices under this provider should use, or null to follow the currency picker.
 * Read by `useFormatPriceString`, so everything downstream — `<Price>`, receipt rows, totals —
 * switches together. Nothing outside that hook should need to call this.
 */
export { useOrderPriceConversion };

type OrderPriceConversionProviderProps = Readonly<{
  /** Raw `currencyConversionData` off the order. Null for USD orders, which need no conversion. */
  conversionData: unknown;
  /** Raw `currencyISO4217` off the order. */
  currency: string | null | undefined;
  children: ReactNode;
}>;

/**
 * Prices amounts in the currency an order was actually paid in, at the rate committed when it was
 * placed — as opposed to the live geo-based rate every other price on Joy follows.
 *
 * A post-purchase receipt is a record of a transaction, so re-converting it at today's rate would
 * make the same order read differently week to week and disagree with what the customer was
 * charged. `PrintPriceCalculator` already gets this treatment via `usePrintPrices`; this is the
 * same override for prices that don't go through the calculator.
 *
 * Missing conversion data means the order was billed in USD with no conversion applied, so it
 * falls back to an identity rate rather than to the live path — matching what `PrintJobDetails`
 * already passes into `StationeryProductPricingContextProvider`. Falling back to the live rate
 * instead would re-price a USD order into the viewer's currency, which is the bug this exists to
 * prevent.
 *
 * Mirrors `usePrintPrices` in letting the order's rate win over the live geo-based rate: that rate
 * governs how we convert live prices for the viewer, not whether we tell the truth about a
 * completed order.
 */
export const OrderPriceConversionProvider = ({ conversionData, currency, children }: OrderPriceConversionProviderProps) => {
  const value = useMemo<OrderPriceConversion>(
    () => ({
      rate: parseOrderCurrencyConversionData(conversionData) ?? DEFAULT_CURRENCY_CONVERSION_RATE,
      currency: (currency || 'USD') as CurrencyCode,
    }),
    [conversionData, currency],
  );

  return <Provider value={value}>{children}</Provider>;
};
